import { inject, Injectable } from '@angular/core';
import { GridStore } from '../stores/grid/grid.state';
import { WidgetStore } from '../stores/grid/widget.state';
import { ConfirmationDialogService } from './confirmation-dialog.service';

const DEFAULT_HEADER = 'Seite wirklich verlassen?';
const DEFAULT_MESSAGE =
  'Ihre Ansicht enthält ungespeicherte Änderungen. Wenn Sie fortfahren, gehen diese verloren.';

/**
 * Single entry point for "discard unsaved atlas changes?" checks. Used by the
 * route guard as well as by in-app actions that throw the current grid away
 * without a guarded navigation (landing page, topic switch, new view), so all
 * of them behave identically.
 */
@Injectable({ providedIn: 'root' })
export class UnsavedChangesService {
  private readonly gridStore = inject(GridStore);
  private readonly widgetStore = inject(WidgetStore);
  private readonly confirmationService = inject(ConfirmationDialogService);

  /**
   * Whether leaving the current grid would lose anything. An empty, unsaved
   * canvas (blank Werkstatt, at most the adder tile) has nothing to lose -
   * whereas emptying an existing bookmark is a real change.
   *
   * @return {boolean} True if there are unsaved changes worth warning about.
   */
  hasUnsavedChanges(): boolean {
    if (!this.gridStore.gridHasChanges()) return false;
    return !!this.gridStore.activeBookmark() || !this.widgetStore.isEmpty();
  }

  /**
   * Resolves immediately with `true` if the grid has no unsaved changes,
   * otherwise asks the user. On confirmation the change flag is reset, so a
   * subsequent guarded navigation does not ask a second time.
   *
   * @param {string} [header] - Optional dialog header.
   * @param {string} [message] - Optional dialog message.
   * @return {Promise<boolean>} Whether the caller may proceed.
   */
  async confirmDiscard(
    header = DEFAULT_HEADER,
    message = DEFAULT_MESSAGE
  ): Promise<boolean> {
    if (!this.hasUnsavedChanges()) return true;

    const confirmed =
      await this.confirmationService.confirmAtlasOverwritePromise(
        header,
        message
      );
    if (confirmed) this.gridStore.resetGridHasChanges();
    return confirmed;
  }
}
